import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faBookmark,
  faLocationDot,
  faVideo,
  faCalendar,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchLiveEventsThunk,
  setLiveFilter,
  selectLiveEvents,
  selectLiveEventsLoading,
  selectLiveEventsFilter,
  selectLiveEventsMonth,
  selectLiveEventsYear,
  selectLiveEventsInitialLoaded,
  selectLiveEventsLastFetched,
} from "../../../store/liveEventsSlice";
import defaultImage from "../default.png";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthsFull = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseUtcDate(rawDate) {
  if (!rawDate) return null;
  if (rawDate instanceof Date) return rawDate;
  let str = String(rawDate).trim();
  if (!str.includes("Z") && !str.includes("+") && !str.match(/-\d{2}:\d{2}$/)) {
    str = str.replace(" ", "T") + "Z";
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? new Date(rawDate) : d;
}

function formatTimeRange(dateStr, durationMinutes = 30) {
  if (!dateStr) return "12:00 PM - 12:30 PM";
  try {
    const start = parseUtcDate(dateStr);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

    const format12Hr = (d) => {
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    };

    let tzCode = "";
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(start);
      tzCode = parts.find((p) => p.type === "timeZoneName")?.value || "";
    } catch {}

    return `${format12Hr(start)} - ${format12Hr(end)}${tzCode ? ` (${tzCode})` : ""}`;
  } catch {
    return "12:00 PM - 12:30 PM";
  }
}

export const EventCard = ({ startMonth, startYear }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const events = useSelector(selectLiveEvents);
  const loading = useSelector(selectLiveEventsLoading);
  const activeFilter = useSelector(selectLiveEventsFilter);
  const month = useSelector(selectLiveEventsMonth);
  const year = useSelector(selectLiveEventsYear);
  const initialLoaded = useSelector(selectLiveEventsInitialLoaded);
  const lastFetched = useSelector(selectLiveEventsLastFetched);

  const showNavigate = activeFilter === "month";

  const fetchEvents = useCallback((filterType, m, y, silent = false) => {
    dispatch(
      fetchLiveEventsThunk({
        filter: filterType,
        month: m !== undefined ? m + 1 : undefined,
        year: y,
        silent,
      })
    );
  }, [dispatch]);

  // Fetch on initial mount only: if not loaded, fetch with loader; if already loaded, revalidate silently
  useEffect(() => {
    if (!initialLoaded) {
      if (activeFilter === "month") {
        fetchEvents("month", month, year, false);
      } else {
        fetchEvents(activeFilter, undefined, undefined, false);
      }
    } else {
      if (activeFilter === "month") {
        fetchEvents("month", month, year, true);
      } else {
        fetchEvents(activeFilter, undefined, undefined, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterClick = (filterType) => {
    if (filterType === activeFilter) return;
    if (filterType === "month") {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      dispatch(setLiveFilter({ filter: "month", month: currentMonth, year: currentYear }));
      fetchEvents("month", currentMonth, currentYear, false);
    } else {
      dispatch(setLiveFilter({ filter: filterType }));
      fetchEvents(filterType, undefined, undefined, false);
    }
  };

  const handlePrevMonth = () => {
    let nextMonth = month - 1;
    let nextYear = year;
    if (nextMonth < 0) {
      nextMonth = 11;
      nextYear = year - 1;
    }
    dispatch(setLiveFilter({ filter: "month", month: nextMonth, year: nextYear }));
    fetchEvents("month", nextMonth, nextYear, false);
  };

  const handleNextMonth = () => {
    let nextMonth = month + 1;
    let nextYear = year;
    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear = year + 1;
    }
    dispatch(setLiveFilter({ filter: "month", month: nextMonth, year: nextYear }));
    fetchEvents("month", nextMonth, nextYear, false);
  };

  return (
    <div className="LiveEventsContainer">
      <div className="filters">
        <button
          className={`filterButton ${activeFilter === "next30days" ? "activeFilterButton" : ""}`}
          onClick={() => handleFilterClick("next30days")}
        >
          <FontAwesomeIcon className="filterIcon" icon={faVideo} />
          Next 30 Days
        </button>
        <button
          className={`filterButton ${activeFilter === "upcoming" ? "activeFilterButton" : ""}`}
          onClick={() => handleFilterClick("upcoming")}
        >
          <FontAwesomeIcon className="filterIcon" icon={faCalendar} />
          All Upcoming Events
        </button>
        <button
          className={`filterButton ${activeFilter === "month" ? "activeFilterButton" : ""}`}
          onClick={() => handleFilterClick("month")}
        >
          <FontAwesomeIcon className="filterIcon" icon={faCalendar} />
          Select Month
        </button>
        <button
          className={`filterButton ${activeFilter === "past" ? "activeFilterButton" : ""}`}
          onClick={() => handleFilterClick("past")}
        >
          <FontAwesomeIcon className="filterIcon" icon={faClock} />
          Past
        </button>
      </div>

      <div className={`navigateFilters ${showNavigate ? "" : "hidden"}`}>
        <button className="navigateButton" onClick={handlePrevMonth}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <p>{`${monthsFull[month]} - ${year}`}</p>
        <button className="navigateButton" onClick={handleNextMonth}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      {loading ? (
        <div className="LiveEventsLoadingContainer">
          <div className="LiveEventsLoadingBanner">
            <div className="LiveEventsMainSpinner"></div>
            <p className="LiveEventsLoadingText">
              {activeFilter === "month"
                ? `Loading live sessions for ${monthsFull[month]} ${year}...`
                : "Loading live guided sessions..."}
            </p>
          </div>
          <div className="LiveEventsSkeletonGrid">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="eventItemCard-skeleton">
                <div className="skeleton-media shimmer">
                  <div className="skeleton-date-badge shimmer"></div>
                </div>
                <div className="skeleton-body">
                  <div className="skeleton-category shimmer"></div>
                  <div className="skeleton-title shimmer"></div>
                  <div className="skeleton-about-row shimmer"></div>
                  <div className="skeleton-btn shimmer"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : events.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#718096" }}>
          <p style={{ fontSize: "1.1rem", fontWeight: 500 }}>No live sessions scheduled for this period.</p>
          <p style={{ fontSize: "0.9rem", marginTop: "0.5rem" }}>Check back soon or explore other months!</p>
        </div>
      ) : (
        <div className="LiveEventsGrid">
          {events.map((event) => {
            const eventDateObj = parseUtcDate(event.event_date);
            const categoryClass = (event.category || "General").replace(/\s+/g, "_");
            const isFuture = event.is_future ?? true;
            const dayFormatted = eventDateObj
              ? String(eventDateObj.getDate()).padStart(2, "0")
              : (event.day ? String(event.day).padStart(2, "0") : "01");
            const monthText = eventDateObj
              ? months[eventDateObj.getMonth()]
              : (event.month ? months[event.month - 1] : "");
            const timeFormatted = formatTimeRange(event.event_date, event.duration_minutes);

            return (
              <div
                key={event.id}
                className={`eventItemCard ${categoryClass} ${isFuture ? "future" : "past"}`}
              >
                <div className="displayImage">
                  <div className="showDate">
                    <div className="eventDay">{dayFormatted}</div>
                    <div className="eventMonth">{monthText}</div>
                  </div>
                  {event.is_registered && (
                    <div className="registeredStatus">
                      Registered
                    </div>
                  )}
                  <img
                    src={event.cover_image}
                    alt={event.title}
                    className="DisplayImage"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultImage;
                    }}
                  />
                </div>

                <div className="eventSubhead">
                  <span className="EventCategory">{event.category}</span>
                  <FontAwesomeIcon className="bookmarkEvent" icon={faBookmark} />
                </div>

                <p className="EventTitle">{event.title}</p>

                <div className="EventAbout">
                  <img
                    src={event.presenter_avatar || defaultImage}
                    alt={event.presenter}
                    className="profileImg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultImage;
                    }}
                  />
                  <span className="Presenter">
                    <p className="subName">Session by</p>
                    <p className="detailName">{event.presenter}</p>
                  </span>
                  <span className="EventTime">
                    <p className="subName">Time</p>
                    <p className="detailName">{timeFormatted}</p>
                  </span>
                </div>

                <div className="eventDetails">
                  <p>
                    <FontAwesomeIcon icon={faLocationDot} style={{ marginRight: 6 }} />
                    Live Stream
                  </p>
                </div>

                <button
                  className={isFuture ? "rsvpEvent" : "NoRSVP"}
                  onClick={() => {
                    if (isFuture) {
                      navigate(`/live/register/${encodeURIComponent(event.title)}/${event.id}`);
                    }
                  }}
                >
                  {event.is_registered ? "Manage RSVP" : "RSVP"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventCard;
